import {
  pgTable,
  uuid,
  text,
  boolean,
  jsonb,
  integer,
  timestamp,
  uniqueIndex,
  index,
  check,
  numeric,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// CAPI Connections — per-tenant, per-platform Conversion API setup
// ---------------------------------------------------------------------------
// Council 9 spec. Stores pixel/tag IDs + encrypted access tokens.
// Tokens MUST be encrypted at rest (DEK). NEVER log tokens.
// Event mapping maps internal events → platform event names.

export const CAPI_PLATFORMS = ["meta", "google", "tiktok"] as const;
export type CapiPlatform = (typeof CAPI_PLATFORMS)[number];

/** Maps internal event types to platform-specific event names */
export interface CapiEventMapping {
  [internalEvent: string]: {
    platformEvent: string; // e.g. "Purchase", "Lead", "CompleteRegistration"
    includeValue: boolean; // send revenue value with event
    enabled: boolean;
  };
}

/** Per-platform configuration differences */
export interface CapiPlatformConfig {
  // Meta: pixel_id, access_token
  // Google: customer_id, conversion_action_id, gclid matching
  // TikTok: pixel_code, access_token, batch upload (24h window)
  customerId?: string; // Google Ads customer ID
  conversionActionId?: string; // Google offline conversion action
  batchWindowHours?: number; // TikTok: default 24h
}

export const capiConnections = pgTable("capi_connections", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  platform: text("platform").notNull(), // 'meta' | 'google' | 'tiktok'
  pixelId: text("pixel_id").notNull(), // Meta pixel ID, Google tag ID, TikTok pixel code
  // Token encrypted at rest — DEK wiring via @ease/crypto when available
  // TODO: Wire DEK encryption (infrastructure dependency on @ease/crypto integration)
  encryptedAccessToken: text("encrypted_access_token").notNull(),
  encryptedDek: text("encrypted_dek"), // nullable until DEK wiring complete
  eventMapping: jsonb("event_mapping").$type<CapiEventMapping>().notNull().default({}),
  platformConfig: jsonb("platform_config").$type<CapiPlatformConfig>().default({}),
  isActive: boolean("is_active").notNull().default(true),
  testEventCode: text("test_event_code"), // Meta test event code for validation
  matchQualityScore: numeric("match_quality_score", { precision: 3, scale: 1 }), // 0.0-10.0
  lastSyncAt: timestamp("last_sync_at", tz),
  errorCount: integer("error_count").notNull().default(0),
  lastError: text("last_error"),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  uniqueIndex("uq_capi_tenant_platform_pixel").on(table.tenantId, table.platform, table.pixelId),
  index("idx_capi_tenant").on(table.tenantId),
  index("idx_capi_tenant_platform").on(table.tenantId, table.platform),
  check("chk_capi_platform", sql`${table.platform} IN ('meta', 'google', 'tiktok')`),
]);
